import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { combineLatest, map, of, switchMap, tap } from 'rxjs';
import { Condition, IntegrationConnection, Operator, ProductGroup } from '../../../generated/generated-types';
import { IntegrationConnectionService } from '../../services/integration-connection.service';
import {
    ProductGroupIntegrationLink,
    ProductGroupIntegrationLinkService,
} from '../../services/product-group-integration-link.service';
import { ProductGroupIntegrationRowComponent } from '../product-group-integration-row/product-group-integration-row.component';

@Component({
    selector: 'app-product-group-integration-list',
    templateUrl: './product-group-integration-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslatePipe, ProgressSpinnerModule, ProductGroupIntegrationRowComponent],
})
export class ProductGroupIntegrationListComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly integrationService = inject(IntegrationConnectionService);
    private readonly linkService = inject(ProductGroupIntegrationLinkService);

    productGroupModel = input.required<ProductGroup>();

    readonly productGroupId = computed(() => this.productGroupModel()?.id);
    readonly isLoading = signal(true);
    readonly links = signal<ProductGroupIntegrationLink[]>([]);

    constructor() {
        toObservable(this.productGroupId)
            .pipe(
                tap(() => this.isLoading.set(true)),
                switchMap(productGroupId => {
                    const integrations$ = this.integrationService
                        .getItems({ take: 100, onlyForProducts: true }, { enableStream: true })
                        .stream$
                        .pipe(map(result => simpleDeepClone(result.getIntegrationConnectionsList)));

                    const links$ = productGroupId
                        ? this.linkService
                            .getItems(
                                {
                                    take: 1000,
                                    searchFilter: {
                                        field: 'productGroupId',
                                        value: productGroupId,
                                        condition: Condition.AND,
                                        operator: Operator.equal,
                                    },
                                },
                                { enableStream: true },
                            )
                            .stream$
                            .pipe(
                                map(result =>
                                    simpleDeepClone(result.getProductGroupIntegrationLinks.items) as ProductGroupIntegrationLink[],
                                ),
                            )
                        : of([] as ProductGroupIntegrationLink[]);

                    return combineLatest([integrations$, links$]).pipe(
                        map(([integrations, shopLinks]) =>
                            this.mergeIntegrationsWithLinks(integrations.items || [], shopLinks, productGroupId),
                        ),
                    );
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: merged => {
                    this.links.set(merged);
                    this.isLoading.set(false);
                },
                error: () => this.isLoading.set(false),
            });
    }

    onLinkChange(updated: ProductGroupIntegrationLink): void {
        this.links.update(current =>
            current.map(link => (link.integrationId === updated.integrationId ? { ...link, ...updated } : link)),
        );
    }

    private mergeIntegrationsWithLinks(
        integrations: IntegrationConnection[],
        shopLinks: ProductGroupIntegrationLink[],
        productGroupId: string | undefined,
    ): ProductGroupIntegrationLink[] {
        const merged = [...shopLinks];
        const byIntegrationId = new Map(merged.map(link => [link.integrationId, link]));

        for (const integration of integrations) {
            const existing = byIntegrationId.get(integration.id);
            if (existing) {
                if (!existing.integration) {
                    existing.integration = integration;
                }
            } else {
                merged.push({
                    integrationId: integration.id,
                    integration,
                    productGroupId,
                    externalEntityId: null,
                    lockShopCategory: false,
                });
            }
        }

        return merged;
    }
}
